import type {Page} from '@PosterWhiteboard/page/page.class';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {ImageSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {PageBackgroundObject} from '@PosterWhiteboard/page/page-background.class';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {UserImageSource} from '@Libraries/user-image-source.constants';
import {UserVideoSource} from '@Libraries/user-video-library';
import {PMWStockImageSource} from '@Libraries/pmw-stock-media-library.types';
import {POSTER_GETTY_LIMIT} from '@PosterWhiteboard/poster/poster.types';
import {isUserPremium} from '@Libraries/user.library';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import {
  openExtractedGettyStickerLimitExceededOnPageDuplicateModal,
  openGettyImageLimitExceededOnPageDuplicateModal,
  openGettyMultipleAssetsLimitExceededOnPageDuplicateModal,
  openGettyVideoLimitExceededOnPageDuplicateModal,
} from '@Components/poster-editor/library/poster-editor-open-modals';

interface GettyLimitExceeded {
  images: boolean;
  videos: boolean;
  stickers: boolean;
}

const filterGettyLimitExceededItemsFromPageObject = (pageObject: PageObject, poster: Poster): {filteredPageObject: PageObject; exceeded: GettyLimitExceeded} => {
  const exceeded: GettyLimitExceeded = {images: false, videos: false, stickers: false};

  const isPremium = isUserPremium();
  let remainingImages = isPremium ? Number.POSITIVE_INFINITY : POSTER_GETTY_LIMIT.IMAGES - poster.getGettyImageCount();
  let remainingVideos = isPremium ? Number.POSITIVE_INFINITY : POSTER_GETTY_LIMIT.VIDEOS - poster.getGettyVideoCount();
  let remainingStickers = isPremium ? Number.POSITIVE_INFINITY : POSTER_GETTY_LIMIT.EXTRACTED_GETTY_STICKER - poster.getExtractedGettyStickerCount();

  let filteredBackground: PageBackgroundObject = pageObject.background;
  if (pageObject.background.details.type === BackgroundTypeName.IMAGE) {
    const bgObject = pageObject.background.details;
    const bgSource = bgObject.imageSource;
    const bgIsPurchased = bgObject.isPurchased;
    if ((bgSource === UserImageSource.GETTY || bgSource === UserImageSource.GETTY_ILLUSTRATIONS) && !bgIsPurchased) {
      if (remainingImages <= 0) {
        exceeded.images = true;
        filteredBackground = {
          details: {
            type: BackgroundTypeName.COLOR,
            uid: pageObject.background.details.uid,
            onPageStartTime: pageObject.background.details.onPageStartTime,
            onPageEndTime: pageObject.background.details.onPageEndTime,
            fill: {fillType: FillTypes.SOLID, fillColor: [[255, 255, 255, 1]]},
          },
        };
      } else {
        remainingImages = remainingImages - 1;
      }
    }
  }

  const filteredItemsHashMap: Record<string, (typeof pageObject.items.itemsHashMap)[string]> = {};
  const filteredItemsOrder: string[] = [];

  for (const uid of pageObject.items.itemsOrder) {
    const item = pageObject.items.itemsHashMap[uid];
    let keepItem = true;

    if (item.gitype === ITEM_TYPE.IMAGE) {
      const imageItem = item as ImageItemObject;
      if ((imageItem.imageSource === UserImageSource.GETTY || imageItem.imageSource === UserImageSource.GETTY_ILLUSTRATIONS) && !imageItem.isPurchased) {
        if (remainingImages <= 0) {
          keepItem = false;
          exceeded.images = true;
        } else {
          remainingImages = remainingImages - 1;
        }
      } else if (imageItem.imageSource === PMWStockImageSource.PMW_EXTRACTED_GETTY_STICKER && !imageItem.isPurchased) {
        if (remainingStickers <= 0) {
          keepItem = false;
          exceeded.stickers = true;
        } else {
          remainingStickers = remainingStickers - 1;
        }
      }
    } else if (item.gitype === ITEM_TYPE.VIDEO) {
      const videoItem = item as VideoItemObject;
      if (videoItem.videoSource === UserVideoSource.GETTY) {
        if (remainingVideos <= 0) {
          keepItem = false;
          exceeded.videos = true;
        } else {
          remainingVideos = remainingVideos - 1;
        }
      }
    } else if (item.gitype === ITEM_TYPE.SLIDESHOW) {
      const slideshowItem = item as SlideshowItemObject;
      let slideshowGettyImages = 0;
      let slideshowGettyVideos = 0;

      for (const slideUid of slideshowItem.slides.slidesOrder) {
        const slide = slideshowItem.slides.slidesHashMap[slideUid];
        if (slide.gitype === ITEM_TYPE.IMAGESLIDE) {
          const imageSlide = slide as ImageSlideItemObject;
          if ((imageSlide.imageSource === UserImageSource.GETTY || imageSlide.imageSource === UserImageSource.GETTY_ILLUSTRATIONS) && !imageSlide.isPurchased) {
            slideshowGettyImages = slideshowGettyImages + 1;
          }
        } else if (slide.gitype === ITEM_TYPE.VIDEOSLIDE) {
          const videoSlide = slide as VideoSlideItemObject;
          if (videoSlide.videoSource === UserVideoSource.GETTY) {
            slideshowGettyVideos = slideshowGettyVideos + 1;
          }
        }
      }

      if (slideshowGettyImages > 0 && slideshowGettyImages > remainingImages) {
        keepItem = false;
        exceeded.images = true;
      } else {
        remainingImages -= slideshowGettyImages;
      }

      if (slideshowGettyVideos > 0 && slideshowGettyVideos > remainingVideos) {
        keepItem = false;
        exceeded.videos = true;
      } else {
        remainingVideos -= slideshowGettyVideos;
      }
    }

    if (keepItem) {
      filteredItemsHashMap[uid] = item;
      filteredItemsOrder.push(uid);
    }
  }

  return {
    filteredPageObject: {
      ...pageObject,
      background: filteredBackground,
      items: {
        itemsHashMap: filteredItemsHashMap,
        itemsOrder: filteredItemsOrder,
        groups: pageObject.items.groups,
      },
    },
    exceeded,
  };
};

export const addNewBlankPage = async (): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }
  const currentPageHashedId = poster.getCurrentPageId();
  const pageOrder = poster.pages.pageOrder;
  const currentPageIndex = pageOrder.indexOf(currentPageHashedId);
  // When no current page is selected (indexOf === -1), append to the end instead of inserting at position 0,
  // which would cause addNewPage to read pagesInOrder[-1].background and crash.
  const targetOrder = currentPageIndex === -1 ? pageOrder.length : currentPageIndex + 1;
  await addNewPage(targetOrder);
};

export const addNewPage = async (order?: number): Promise<void> => {
  if (!window.posterEditor?.whiteboard?.pages) {
    throw Error('No poster found to add page. Initialize a poster first');
  }

  const pagesInOrder = window.posterEditor.whiteboard.pages.getPagesInOrder();
  const pageOrder = order ?? pagesInOrder?.length;

  if (pageOrder === window.PMW.MAX_NUMBER_OF_PAGES) {
    return;
  }

  const page = await window.posterEditor.whiteboard.pages.addPage(
    {
      background: pagesInOrder[pageOrder - 1].background?.toObject(),
    },
    {pageOrder}
  );
  scrollToPage(page.hashedID);
  page.poster.setCurrentPage(page.hashedID);
};

export const scrollToPage = (hashedId: string, animate = true): void => {
  const ANIMATION_DURATION = 300;
  const page = window.posterEditor?.whiteboard?.pages.getPage(hashedId);
  const pageOffsetTop = page?.pageContainer.offsetTop;
  const whiteboardContainer = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER];
  if (whiteboardContainer && typeof pageOffsetTop === 'number') {
    page?.refreshFullCanvas();
    if (animate) {
      whiteboardContainer.animate({scrollTop: pageOffsetTop}, ANIMATION_DURATION, () => {
        window.posterEditor?.whiteboard?.refreshAllVisibleCanvases();
      });
    } else {
      whiteboardContainer.scrollTop(pageOffsetTop);
      window.posterEditor?.whiteboard?.refreshAllVisibleCanvases();
    }
  }
};

export const addDuplicatedPage = async (pageToDuplicate: Page): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  const pages = poster?.pages;
  if (!pages || !poster) {
    throw Error('No poster found to add page. Initialize a poster first');
  }

  if (pages.pageOrder.length >= window.PMW.MAX_NUMBER_OF_PAGES) {
    return;
  }

  const duplicatedPageObject = pageToDuplicate.getDuplicatedPageObject();
  const {filteredPageObject, exceeded} = filterGettyLimitExceededItemsFromPageObject(duplicatedPageObject, poster);

  const pageOrder = (pages.getPageOrder(pageToDuplicate.hashedID) ?? 0) + 1;
  const page = await pages.addPage(filteredPageObject, {pageOrder, updateRedux: true, immediatelySelectOnceAdded: true});

  scrollToPage(page.hashedID);

  const exceededCount = [exceeded.images, exceeded.videos, exceeded.stickers].filter(Boolean).length;
  if (exceededCount > 1) {
    openGettyMultipleAssetsLimitExceededOnPageDuplicateModal();
  } else if (exceeded.images) {
    openGettyImageLimitExceededOnPageDuplicateModal();
  } else if (exceeded.videos) {
    openGettyVideoLimitExceededOnPageDuplicateModal();
  } else if (exceeded.stickers) {
    openExtractedGettyStickerLimitExceededOnPageDuplicateModal();
  }
};
